import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardErrorState, DashboardNarrativePanel } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { useDashboardPdfRowOverride } from "src/hooks/UseDashboardPdfTableExpansion";
import type { PaidMediaOverviewCampaignRow } from "src/services/PaidMediaOverviewAPI";
import PaidMediaOverviewHeader, {
  type PaidMediaOverviewHeaderState,
} from "./PaidMediaOverviewHeader";
import OverviewWarningsBanner from "./OverviewWarningsBanner";
import OverviewKpiCards from "./OverviewKpiCards";
import PlatformComparisonCards from "./PlatformComparisonCards";
import SpendRevenueTrendCard from "./SpendRevenueTrendCard";
import PlatformContributionCard from "./PlatformContributionCard";
import EfficiencyRow from "./EfficiencyRow";
import ConversionRow from "./ConversionRow";
import InvestmentEfficiencyCard from "./InvestmentEfficiencyCard";
import QuadrantCard from "./QuadrantCard";
import RevenueValidationCard from "./RevenueValidationCard";
import AttributionConfidenceCard from "./AttributionConfidenceCard";
import CampaignLeaderboard, {
  type CampaignLeaderboardFilters,
  type CampaignTableFilters,
} from "./CampaignLeaderboard";
import CampaignDetailDrawer, {
  campaignSelectionFromRow,
  campaignSelectionFromScalePoint,
  type PaidMediaOverviewCampaignSelection,
} from "./CampaignDetailDrawer";
import {
  OverviewAttributionProvider,
  toOverviewAttributionSource,
  toPaidMediaAttributionMode,
} from "../../utils/PaidMediaOverviewAttributionContext";
import { overviewPlatformLabel, overviewStatusLabel } from "../../utils/PaidMediaOverviewLabels";
import { downloadCsv } from "../../utils/PaidMediaCsvExport";
import {
  usePaidMediaOverview,
  usePaidMediaOverviewCampaignDetail,
  usePaidMediaOverviewCampaigns,
} from "../../hooks";
import "./PaidMediaOverviewDashboard.scss";

const DEFAULT_RANGE_DAYS = 30;
const CAMPAIGN_PAGE_SIZE = 10;
const CAMPAIGN_SORT_BY = "revenue";
const CAMPAIGN_SORT_ORDER = "desc";
const CSV_FILENAME = "paid-media-campaign-leaderboard.csv";

const csvNumber = (value: number | null | undefined, digits?: number): number | string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "";
  }
  return digits === undefined ? value : Number(value.toFixed(digits));
};

const PaidMediaOverviewDashboard: FC = () => {
  const { t } = useTranslation();

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);
  const [headerState, setHeaderState] = useState<PaidMediaOverviewHeaderState>(() => ({
    attribution: "platform",
    granularity: "daily",
    startDate: dayjs().subtract(DEFAULT_RANGE_DAYS, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    platform: "all",
    campaignId: "",
    objectiveOrNetwork: "",
    status: "",
  }));
  
  const [tableFilters, setTableFilters] = useState<CampaignTableFilters>({
    platform: "all",
    objectiveOrNetwork: "",
    status: "",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
 
  const [selectedCampaign, setSelectedCampaign] =
    useState<PaidMediaOverviewCampaignSelection | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const attributionMode = toPaidMediaAttributionMode(headerState.attribution);

  const patchHeaderState = useCallback((patch: Partial<PaidMediaOverviewHeaderState>) => {
    setHeaderState(current => ({ ...current, ...patch }));
    if (patch.platform !== undefined) {
      const nextPlatform = patch.platform;
      setTableFilters(current => ({ ...current, platform: nextPlatform }));
    }
    setPage(1);
  }, []);

  const overview = usePaidMediaOverview({
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    granularity: headerState.granularity,
    platform: headerState.platform,
    attributionMode,
    campaignId: headerState.campaignId,
    objectiveOrNetwork: headerState.objectiveOrNetwork,
    status: headerState.status,
  });

  const availableFilters = overview.filters;
  useEffect(() => {
    if (!availableFilters) {
      return;
    }

    const isStaleObjective = (value: string) =>
      Boolean(value) && !availableFilters.objectives_or_networks?.includes(value);
    const isStaleStatus = (value: string) =>
      Boolean(value) && !availableFilters.statuses?.some(status => status?.value === value);

    const patch: Partial<PaidMediaOverviewHeaderState> = {};
    if (
      headerState.campaignId &&
      !availableFilters.campaigns?.some(
        campaign => campaign?.campaign_id === headerState.campaignId
      )
    ) {
      patch.campaignId = "";
    }
    if (isStaleObjective(headerState.objectiveOrNetwork)) {
      patch.objectiveOrNetwork = "";
    }
    if (isStaleStatus(headerState.status)) {
      patch.status = "";
    }

    const tablePatch: Partial<CampaignTableFilters> = {};
    if (isStaleObjective(tableFilters.objectiveOrNetwork)) {
      tablePatch.objectiveOrNetwork = "";
    }
    if (isStaleStatus(tableFilters.status)) {
      tablePatch.status = "";
    }

    if (Object.keys(patch).length) {
      setHeaderState(current => ({ ...current, ...patch }));
    }
    if (Object.keys(tablePatch).length) {
      setTableFilters(current => ({ ...current, ...tablePatch }));
    }
    if (Object.keys(patch).length || Object.keys(tablePatch).length) {
      setPage(1);
    }
  }, [
    availableFilters,
    headerState.campaignId,
    headerState.objectiveOrNetwork,
    headerState.status,
    tableFilters.objectiveOrNetwork,
    tableFilters.status,
  ]);

  const campaigns = usePaidMediaOverviewCampaigns({
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    platform: tableFilters.platform,
    campaignId: headerState.campaignId,
    objectiveOrNetwork: tableFilters.objectiveOrNetwork || headerState.objectiveOrNetwork,
    status: tableFilters.status || headerState.status,
    search,
    page,
    pageSize: CAMPAIGN_PAGE_SIZE,
    sortBy: CAMPAIGN_SORT_BY,
    sortOrder: CAMPAIGN_SORT_ORDER,
  });

  const {
    rows: exportLeaderboardRows,
    onBeforeCapture: onBeforeLeaderboardCapture,
    onAfterCapture: onAfterLeaderboardCapture,
  } = useDashboardPdfRowOverride(campaigns.rows, () => campaigns.fetchAllRows());

  const campaignDetail = usePaidMediaOverviewCampaignDetail({
    platform: selectedCampaign?.platform ?? null,
    campaignId: selectedCampaign?.campaign_id ?? null,
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    granularity: headerState.granularity,
    attributionMode,
  });

  const summaryPlatforms = overview.summary?.platforms ?? [];
  const trendSeries = useMemo(() => overview.trends?.series ?? [], [overview.trends]);
  const purchasesPeriodTotal =
    overview.summary?.combined?.find(kpi => kpi?.id === "paid-purchases")?.formatted_value ?? null;
  const currency = overview.currency ?? campaigns.currency;

  const allWarnings = useMemo(() => {
    const seen = new Set<string>();
    return [...overview.warnings, ...campaigns.warnings].filter(warning => {
      if (!warning?.message) {
        return false;
      }
      const key = `${warning.code ?? ""}|${warning.message}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [overview.warnings, campaigns.warnings]);

  const leaderboardFilters: CampaignLeaderboardFilters = {
    platform: tableFilters.platform,
    objectiveOrNetwork: tableFilters.objectiveOrNetwork,
    status: tableFilters.status,
    search,
  };

  const handleLeaderboardFiltersChange = useCallback(
    (patch: Partial<CampaignLeaderboardFilters>) => {
      const { search: nextSearch, ...tablePatch } = patch;
      if (Object.keys(tablePatch).length) {
        setTableFilters(current => ({ ...current, ...tablePatch }));
        setPage(1);
      }
      if (nextSearch !== undefined) {
        setSearch(nextSearch);
        setPage(1);
      }
    },
    []
  );

  const { fetchAllRows } = campaigns;

  const handleExport = useCallback(async () => {
    setExportError(null);

    let exportRows: PaidMediaOverviewCampaignRow[];
    try {
      exportRows = await fetchAllRows();
    } catch (reason) {
      setExportError(
        reason instanceof Error
          ? reason.message
          : t("paidMediaOverview.errors.requestFailed")
      );
      return;
    }

    downloadCsv(
      CSV_FILENAME,
      [
        t("paidMediaOverview.leaderboard.columnPlatform"),
        t("paidMediaOverview.leaderboard.columnCampaign"),
        t("paidMediaOverview.leaderboard.columnObjective"),
        t("paidMediaOverview.leaderboard.columnStartDate"),
        t("paidMediaOverview.metrics.spend"),
        t("paidMediaOverview.metrics.impressions"),
        t("paidMediaOverview.metrics.clicks"),
        t("paidMediaOverview.metrics.ctr"),
        t("paidMediaOverview.metrics.purchases"),
        t("paidMediaOverview.metrics.revenue"),
        t("paidMediaOverview.metrics.roas"),
        t("paidMediaOverview.metrics.costPerPurchase"),
        t("paidMediaOverview.metrics.spendShare"),
        t("paidMediaOverview.metrics.revenueShare"),
        t("paidMediaOverview.leaderboard.columnStatus"),
      ],
      exportRows.map(row => [
        overviewPlatformLabel(t, row?.platform),
        row?.campaign_name ?? "",
        row?.objective_or_network ?? "",
        row?.start_date ?? "",
        csvNumber(row?.spend),
        csvNumber(row?.impressions),
        csvNumber(row?.clicks),
        csvNumber(row?.ctr, 2),
        csvNumber(row?.purchases),
        csvNumber(row?.revenue),
        csvNumber(row?.roas, 2),
        csvNumber(row?.cost_per_purchase, 2),
        csvNumber(row?.spend_share, 1),
        csvNumber(row?.revenue_share, 1),
        overviewStatusLabel(t, row?.status_code, row?.status_label),
      ])
    );
  }, [fetchAllRows, t]);

  const narrativeData = useMemo(
    () => ({
      summary: overview.summary,
      funnel: overview.funnel,
      revenue_validation: overview.revenueValidation,
      daily_performance: trendSeries,
      attribution_mode: overview.appliedAttributionMode,
    }),
    [
      overview.summary,
      overview.funnel,
      overview.revenueValidation,
      overview.appliedAttributionMode,
      trendSeries,
    ]
  );

  const {
    narrative: overviewNarrative,
    loading: narrativeLoading,
    aiSummary,
  } = useDashboardNarrative({
    dashboardKey: "paid_media",
    tabKey: "paid_media_overview",
    startDate: headerState.startDate,
    endDate: headerState.endDate,
    data: narrativeData,
    enabled: Boolean(overview.summary),
    promptVersion: "v6",
  });

  const allErrors = [...overview.errors, campaigns.error, exportError].filter(
    (error): error is string => Boolean(error)
  );

  const isLoading =
    overview.summaryLoading ||
    overview.trendsLoading ||
    overview.funnelLoading ||
    overview.efficiencyScaleLoading ||
    overview.revenueValidationLoading;

  return (
    <OverviewAttributionProvider
      value={toOverviewAttributionSource(overview.appliedAttributionMode)}
    >
      <div
        className={`paid-media-overview${isPdfCapturing ? " paid-media-overview--pdf-capture" : ""}`}
        ref={pdfContainerRef}
      >
        <PaidMediaOverviewHeader
          state={headerState}
          filters={overview.filters}
          onChange={patchHeaderState}
          shopifyAvailable={overview.shopifyAvailable}
          pdfContainerRef={pdfContainerRef}
          exportDisabled={isLoading}
          onBeforePdfCapture={async () => {
            setIsPdfCapturing(true);
            await onBeforeLeaderboardCapture();
          }}
          onAfterPdfCapture={() => {
            onAfterLeaderboardCapture();
            setIsPdfCapturing(false);
          }}
        />

        {allErrors.map(error => (
          <DashboardErrorState key={error} message={error} />
        ))}

        <OverviewWarningsBanner warnings={allWarnings} />

        {isLoading ? (
          <LoadingSpinner compact />
        ) : (
          <>
            <div data-pdf-block="narrative">
              <DashboardNarrativePanel
                narrative={overviewNarrative}
                loading={narrativeLoading}
                aiSummary={aiSummary}
              />
            </div>
            <div data-pdf-block="kpi-cards">
              <OverviewKpiCards
                combined={overview.summary?.combined ?? []}
                leadingPlatform={overview.summary?.leading_platform ?? null}
              />
            </div>
            <div data-pdf-block="platform-comparison">
              <PlatformComparisonCards
                platforms={summaryPlatforms}
                revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
                efficiencyGap={overview.summary?.efficiency_gap ?? null}
              />
            </div>
            <div className="paid-media-overview__trend-grid" data-pdf-block="spend-revenue-trend">
              <SpendRevenueTrendCard
                series={trendSeries}
                granularity={headerState.granularity}
                leadingPlatform={overview.summary?.leading_platform ?? null}
                currency={currency}
                loading={overview.trendsLoading}
              />
              <PlatformContributionCard
                platforms={summaryPlatforms}
                revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
              />
            </div>
            <div data-pdf-block="efficiency">
            <EfficiencyRow
              series={trendSeries}
              platforms={summaryPlatforms}
              granularity={headerState.granularity}
              roasBenchmark={overview.efficiencyScale?.benchmarks?.roas_benchmark ?? null}
              currency={currency}
              loading={overview.trendsLoading}
            />
            </div>
            <div data-pdf-block="conversion">
            <ConversionRow
              series={trendSeries}
              granularity={headerState.granularity}
              funnelPlatforms={overview.funnel?.platforms ?? []}
              purchasesPeriodTotal={purchasesPeriodTotal}
              trendsLoading={overview.trendsLoading}
              funnelLoading={overview.funnelLoading}
            />
            </div>
            <div data-pdf-block="investment-efficiency">
            <InvestmentEfficiencyCard
              platforms={summaryPlatforms}
              revenueContributionLeader={overview.summary?.revenue_contribution_leader ?? null}
            />
            </div>
            <div data-pdf-block="quadrant">
            <QuadrantCard
              platformView={overview.efficiencyScale?.platform_view ?? []}
              campaignView={overview.efficiencyScale?.campaign_view ?? []}
              benchmarks={overview.efficiencyScale?.benchmarks ?? null}
              currency={currency}
              loading={overview.efficiencyScaleLoading}
              onCampaignSelect={point => {
                const selection = campaignSelectionFromScalePoint(point);
                if (selection) {
                  setSelectedCampaign(selection);
                }
              }}
            />
            </div>
            <div className="paid-media-overview__trend-grid" data-pdf-block="revenue-validation">
              <RevenueValidationCard
                platforms={overview.revenueValidation?.platforms ?? []}
                shopifyAvailable={overview.shopifyAvailable}
                currency={currency}
                loading={overview.revenueValidationLoading}
                campaignFilterActive={Boolean(headerState.campaignId)}
              />
              <AttributionConfidenceCard
                data={overview.attributionConfidence}
                loading={overview.attributionConfidenceLoading}
                shopifyUnavailable={overview.attributionConfidenceErrorCode === "NO_SOURCE_AVAILABLE"}
              />
            </div>
            <div data-pdf-block="campaign-leaderboard" data-pdf-splittable>
            <CampaignLeaderboard
              rows={exportLeaderboardRows}
              filters={leaderboardFilters}
              objectiveOptions={overview.filters?.objectives_or_networks ?? []}
              statusOptions={overview.filters?.statuses ?? []}
              currentPage={campaigns.page}
              totalPages={campaigns.totalPages}
              loading={campaigns.loading}
              exporting={campaigns.exporting}
              onFiltersChange={handleLeaderboardFiltersChange}
              onPageChange={setPage}
              onSelect={row => setSelectedCampaign(campaignSelectionFromRow(row))}
              onExport={handleExport}
            />
            </div>
          </>
        )}

        <CampaignDetailDrawer
          selectedRow={selectedCampaign}
          detail={campaignDetail.detail}
          currency={campaignDetail.currency ?? currency}
          loading={campaignDetail.loading}
          error={campaignDetail.error}
          notFound={campaignDetail.notFound}
          onOpenChange={open => {
            if (!open) {
              setSelectedCampaign(null);
            }
          }}
        />
      </div>
    </OverviewAttributionProvider>
  );
};

export default PaidMediaOverviewDashboard;
